import { Button, FormControlLabel, MenuItem, Switch, Tooltip } from "@mui/material";

import { IRules, MonthDayTypes, MonthPeriodTypes, ExclusionScheduledVigencyType, MovementType, IPossibleMovement, OperationType } from "../../Setup.types";
import { Container, Row, Title, SelectForm, TextField, ButtonDelete } from "./Rules.styled";
import { useQuery } from "@tanstack/react-query";
import api from "services/api";
import { MultiSelectWithSelectAll } from "v2/components/ui";
import { DOCUMENTOS } from "v2/pages/Workspace/Activity/documentos-obrigatorios";
import { DocumentosPorParentesco } from "./DocumentosPorParentesco";

interface IProps {
  rules: IRules;
  handleChange: (payload: Partial<IRules>) => void;
}

export function RulesTabOperation({ rules, handleChange }: IProps) {
  const { inclusion, exclusion, transference, change, possibleMovements = [] } = rules.rulesOperation;
  const allowedDependentTypes = rules.rulesAddressability?.allowedDependentTypes;
  /** Configuração da regra ativa: os documentos por parentesco ficam numa linha própria, abaixo dos campos da regra. */
  const CONFIG_DA_REGRA = { IMEDIATA: "configImmediate", MENSAL: "configMonthly", PROGRAMADA: "configScheduled" } as const;
  const inclusionConfig = CONFIG_DA_REGRA[inclusion.rule as keyof typeof CONFIG_DA_REGRA];
  const exclusionConfig = CONFIG_DA_REGRA[exclusion.rule as keyof typeof CONFIG_DA_REGRA];

  const { data, isFetching, isFetched } = useQuery({
    queryKey: ["organization-roles", rules.rulesRegister.buyerFederalTaxId],
    queryFn: () => api.get(`/v2//operation/organization-roles/${rules.rulesRegister.buyerFederalTaxId}`),
    select: (data) => data?.data?.data as string[],
  });

  if (!isFetched && isFetching) return <div>Loading...</div>;

  /** Opções possíveis da coluna de Cargo. */
  const roleOptions = data ?? [];
  /** Opções possíveis da coluna de Alteraçoes. */
  const changeOptions = ["Nome", "Plano", "Endereço", "E-mail", "Cargo", "Telefone", "Grau de Parentesco", "Nome da Mãe", "Estado Civil", "Data de nascimento", "Data de Admissão", "Data de Demissão"] as const;
  /** Opções possíveis para coluna de Documentos. */
  // const documentOptions = ["CERTIFICADO_NASCIMENTO", "CERTIFICADO_CASAMENTO"] as const;
  const documentOptions = DOCUMENTOS;

  /** Função responsável especificamente por receber os valores de inclusão alterados e formatar corretamente para handleChange. */
  function handleChangeInclusion(payload: Partial<typeof inclusion>) {
    const updatedInclusionFields = { ...inclusion, ...payload };

    return handleChange({ ...rules, rulesOperation: { ...rules.rulesOperation, inclusion: updatedInclusionFields }});
  }

  /** Função responsável especificamente por receber os valores de exclusão alterados e formatar corretamente para handleChange. */
  function handleChangeExclusion(payload: Partial<typeof exclusion>) {
    const updatedExclusionFields = { ...exclusion, ...payload };

    return handleChange({ ...rules, rulesOperation: { ...rules.rulesOperation, exclusion: updatedExclusionFields }});
  }

  /** Função responsável especificamente por receber os valores de transferência alterados e formatar corretamente para handleChange. */
  function handleChangeTransfer(payload: Partial<typeof transference>) {
    const updatedTransferFields = { ...transference, ...payload };

    return handleChange({ ...rules, rulesOperation: { ...rules.rulesOperation, transference: updatedTransferFields }});
  }

  /** Função responsável por adicionar novo campo vazio à array de change. */
  function handleAddChange() {
    const newChange = { type: undefined, mandatoryDocuments: undefined };

    return handleChange({ ...rules, rulesOperation: { ...rules.rulesOperation, change: [...change, newChange] }});
  }

  /** Função responsável por adicionar campos à array de changes individualmente. */
  function handleUpdateChange(payload: Partial<typeof change[0]>, index: number) {
    const updatedPayload = change.map((c, i) => i === index ? { ...c, ...payload } : c);

    return handleChange({ ...rules, rulesOperation: { ...rules.rulesOperation, change: updatedPayload }});
  }

  /** Função responsável por remover campo da array de changes. */
  function handleRemoveChange(index: number) {
    const updatedPayload = change.filter((_c, i) => i !== index);

    return handleChange({ ...rules, rulesOperation: { ...rules.rulesOperation, change: updatedPayload }});
  }

  /** Função responsável por adicionar nova linha vazia à array de possibleMovements. */
  function handleAddPossibleMovement() {
    const newMovement: IPossibleMovement = { operations: [], operatorCode: "" };

    return handleChange({ ...rules, rulesOperation: { ...rules.rulesOperation, possibleMovements: [...possibleMovements, newMovement] }});
  }

  /** Função responsável por adicionar campos à array de possibleMovements individualmente. */
  function handleUpdatePossibleMovement(payload: Partial<IPossibleMovement>, index: number) {
    const updatedPayload = possibleMovements.map((m, i) => i === index ? { ...m, ...payload } : m);

    return handleChange({ ...rules, rulesOperation: { ...rules.rulesOperation, possibleMovements: updatedPayload }});
  }

  /** Função responsável por remover campo da array de possibleMovements. */
  function handleRemovePossibleMovement(index: number) {
    const updatedPayload = possibleMovements.filter((_m, i) => i !== index);

    return handleChange({ ...rules, rulesOperation: { ...rules.rulesOperation, possibleMovements: updatedPayload }});
  }

  /** Verifica se uma operação já foi selecionada em outra linha. */
  function isOperationUsed(operation: OperationType, currentIndex: number): boolean {
    return possibleMovements.some((movement, idx) =>
      idx !== currentIndex && movement.operations.includes(operation)
    );
  }

  return (
    <Container>
      <Title>Inclusão</Title>
      <Row>
        <SelectForm
          label="Regra da movimentação"
          value={inclusion.rule}
          onChange={(e) => handleChangeInclusion({ rule: e.target.value as MovementType })}
        >
          <MenuItem value={"IMEDIATA"}>Imediata</MenuItem>
          <MenuItem value={"MENSAL"}>Mensal</MenuItem>
          <MenuItem value={"PROGRAMADA"}>Programada</MenuItem>
        </SelectForm>
        {inclusion.rule === "IMEDIATA" && (
          <>
            {/* Spec 1 (portão temporal): só na regra imediata; ausente na exclusão por desenho. */}
            <Tooltip
              arrow
              title="Quem for detectado antes da data de admissão fica pronto para envio, com a vigência na data de admissão. Confirme antes com a operadora que ela aceita inclusão com data futura."
            >
              <FormControlLabel
                label="Liberar inclusão antes da data de admissão"
                control={
                  <Switch
                    checked={inclusion.configImmediate.allowEarlyRelease === true}
                    onChange={(e) => handleChangeInclusion({ configImmediate: { ...inclusion.configImmediate, allowEarlyRelease: e.target.checked }})}
                  />
                }
              />
            </Tooltip>
          </>
        )}
        {inclusion.rule === "MENSAL" && (
          <>
            <SelectForm
              label="Data inicio de vigência"
              value={inclusion.configMonthly.vigencyStartDate}
              onChange={(e) => handleChangeInclusion({ configMonthly: { ...inclusion.configMonthly, vigencyStartDate: e.target.value as MonthDayTypes }})}
            >
              <MenuItem value={"DAY_15"}>Dia 15 do mês</MenuItem>
            </SelectForm>
            <MultiSelectWithSelectAll
              label="Cargos não sujeitos a regra"
              value={inclusion.configMonthly.exceptionRoles ?? []}
              options={roleOptions}
              onChange={(values) => handleChangeInclusion({ configMonthly: { ...inclusion.configMonthly, exceptionRoles: values }})}
            />
          </>
        )}
        {inclusion.rule === "PROGRAMADA" && (
          <>
            <TextField
              label="Dias após admissão para processamento"
              type="number"
              inputProps={{ min: 0 }}
              value={inclusion.configScheduled.daysAfterAdmission ?? 0}
              onChange={(e) => handleChangeInclusion({ configScheduled: { ...inclusion.configScheduled, daysAfterAdmission: Number(e.target.value) ?? 0 }})}
            />
            <TextField
              label="Data corte"
              type="number"
              inputProps={{ min: 1, max: 31 }}
              // min/max num TextField fora de <form> nao validam nada: o valor e recortado
              // a 1..31 na escrita, que e o unico contrato que o backend aceita (plano 1).
              value={inclusion.configScheduled.deadline ?? ''}
              onChange={(e) => {
                const raw = Number(e.target.value)
                const deadline = Number.isFinite(raw) && raw > 0 ? Math.min(31, Math.max(1, Math.trunc(raw))) : 1
                handleChangeInclusion({ configScheduled: { ...inclusion.configScheduled, deadline }})
              }}
            />
            <SelectForm
              label="Inicio vigência pós data corte"
              value={inclusion.configScheduled.vigencyAfterDeadline}
              onChange={(e) => handleChangeInclusion({ configScheduled: { ...inclusion.configScheduled, vigencyAfterDeadline: e.target.value as MonthPeriodTypes }})}
            >
              <MenuItem value={"MONTHLY_START"}>Inicio do próximo mês</MenuItem>
              <MenuItem value={"MONTHLY_END"}>Final do mês atual</MenuItem>
            </SelectForm>
          </>
        )}
      </Row>
      {inclusion.rule === "PROGRAMADA" && (
        <Row>
          <MultiSelectWithSelectAll
            label="Cargos não sujeitos a regra"
            value={inclusion.configScheduled.exceptionRoles ?? []}
            options={roleOptions}
            onChange={(values) => handleChangeInclusion({ configScheduled: { ...inclusion.configScheduled, exceptionRoles: values }})}
          />
        </Row>
      )}
      {inclusionConfig && (
        <Row>
          <DocumentosPorParentesco
            lista={inclusion[inclusionConfig]}
            allowedDependentTypes={allowedDependentTypes}
            onChange={(lista) => handleChangeInclusion({ [inclusionConfig]: { ...inclusion[inclusionConfig], ...lista } })}
          />
        </Row>
      )}
      <Title>Exclusão</Title>
      <Row>
        <SelectForm
          label="Data para encerramento do plano"
          value={exclusion.exclusionDateType || 'DISMISSAL_DATE'}
          onChange={(e) => handleChangeExclusion({ exclusionDateType: e.target.value as 'DISMISSAL_DATE' | 'PRIOR_NOTICE_END_DATE' })}
          style={{ minWidth: '300px' }}
        >
          <MenuItem value="DISMISSAL_DATE">Desligamento</MenuItem>
          <MenuItem value="PRIOR_NOTICE_END_DATE">Desligamento ou Término do aviso prévio</MenuItem>
        </SelectForm>
      </Row>
      <Row>
        <SelectForm
          label="Regra da movimentação"
          value={exclusion.rule}
          onChange={(e) => handleChangeExclusion({ rule: e.target.value as MovementType })}
        >
          <MenuItem value={"IMEDIATA"}>Imediata</MenuItem>
          <MenuItem value={"MENSAL"}>Mensal</MenuItem>
          <MenuItem value={"PROGRAMADA"}>Programada</MenuItem>
        </SelectForm>
        {exclusion.rule === "MENSAL" && (
          <>
            <SelectForm
              label="Data fim de vigência"
              value={exclusion.configMonthly.vigencyStartDate}
              onChange={(e) => handleChangeExclusion({ configMonthly: { ...exclusion.configMonthly, vigencyStartDate: e.target.value as MonthPeriodTypes }})}
            >
              <MenuItem value={"MONTHLY_START"}>Inicio do próximo mês</MenuItem>
              <MenuItem value={"MONTHLY_END"}>Final do mês atual</MenuItem>
            </SelectForm>
          </>
        )}
        {exclusion.rule === "PROGRAMADA" && (
          <>
            <TextField
              label="Data corte"
              type="number"
              inputProps={{ min: 1, max: 31 }}
              value={exclusion.configScheduled.deadline ?? ""}
              onChange={(e) =>
                handleChangeExclusion({
                  configScheduled: {
                    ...exclusion.configScheduled,
                    deadline: Number(e.target.value) || undefined,
                  },
                })
              }
            />
            <SelectForm
              label="Data fim de vigência"
              value={exclusion.configScheduled.vigencyStartDate}
              onChange={(e) => handleChangeExclusion({ configScheduled: { ...exclusion.configScheduled, vigencyStartDate: e.target.value as ExclusionScheduledVigencyType }})}
            >
              <MenuItem value={"MONTHLY_START"}>Inicio do próximo mês</MenuItem>
              <MenuItem value={"MONTHLY_END"}>Final do mês atual</MenuItem>
              <MenuItem value={"CUTOFF_DATE"}>Data corte</MenuItem>
            </SelectForm>
            <MultiSelectWithSelectAll
              label="Cargos não sujeitos a regra"
              value={exclusion.configScheduled.exceptionRoles ?? []}
              options={roleOptions}
              onChange={(values) => handleChangeExclusion({ configScheduled: { ...exclusion.configScheduled, exceptionRoles: values }})}
            />
          </>
        )}
      </Row>
      {exclusionConfig && (
        <Row>
          <DocumentosPorParentesco
            lista={exclusion[exclusionConfig]}
            allowedDependentTypes={allowedDependentTypes}
            onChange={(lista) => handleChangeExclusion({ [exclusionConfig]: { ...exclusion[exclusionConfig], ...lista } })}
          />
        </Row>
      )}
      <Title>Transferência</Title>
      <Row>
        <MultiSelectWithSelectAll
          label="Documentos"
          value={transference.mandatoryDocuments ?? []}
          options={[...documentOptions]}
          onChange={(values) => handleChangeTransfer({ mandatoryDocuments: values })}
        />
      </Row>
      <Title>Alteração</Title>
      {change.map((row, index) => (
        <Row key={Math.random()} style={{ alignItems: "center"}}>
          <SelectForm
            label="Tipo"
            value={row.type}
            onChange={(e) => handleUpdateChange({ type: e.target.value as string }, index)}
          >
            {changeOptions.map(option => (
              <MenuItem key={option} value={option} disabled={change.map(c => c.type).includes(option)}>
                {option}
              </MenuItem>
            ))}
          </SelectForm>
          <MultiSelectWithSelectAll
            label="Documentos"
            value={row.mandatoryDocuments ?? []}
            options={[...documentOptions]}
            onChange={(values) => handleUpdateChange({ mandatoryDocuments: values }, index)}
          />
          <ButtonDelete color="error" onClick={() => handleRemoveChange(index)} />
        </Row>
      ))}
      <Button disabled={change.length === changeOptions.length} onClick={handleAddChange}>+ Adicionar alteração</Button>
      <Title>Código Operadora (EB)</Title>
      {possibleMovements.map((row, index) => (
        <Row key={index} style={{ alignItems: "center" }}>
          <SelectForm
            label="Operação"
            multiple
            value={row.operations}
            onChange={(e) => handleUpdatePossibleMovement({ operations: e.target.value as OperationType[] }, index)}
          >
            <MenuItem value="inclusion" disabled={isOperationUsed("inclusion", index)}>
              Inclusão
            </MenuItem>
            <MenuItem value="exclusion" disabled={isOperationUsed("exclusion", index)}>
              Exclusão
            </MenuItem>
            <MenuItem value="change" disabled={isOperationUsed("change", index)}>
              Alterações
            </MenuItem>
            <MenuItem value="transfer" disabled={isOperationUsed("transfer", index)}>
              Transferências
            </MenuItem>
          </SelectForm>
          <TextField
            label="Código Operadora (EB)"
            value={row.operatorCode}
            onChange={(e) => handleUpdatePossibleMovement({ operatorCode: e.target.value }, index)}
          />
          <ButtonDelete color="error" onClick={() => handleRemovePossibleMovement(index)} />
        </Row>
      ))}
      <Button onClick={handleAddPossibleMovement}>+ Adicionar</Button>
    </Container>
  );
}
